import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const ROLES = [
  "Registered Nurse (RN)",
  "Licensed Practical Nurse (LPN)",
  "Certified Nursing Assistant (CNA)",
  "Home Health Aide",
  "Caregiver",
  "Medical Assistant",
  "Phlebotomist",
  "Physical Therapy Aide",
  "Medical Interpreter",
  "Front Desk / Admin",
  "Other",
];
const TYPES = ["Full-time", "Part-time", "Per Diem", "Contract"];
const SHIFTS = ["Any", "Day", "Night", "Rotating"];

export default function CreateJob() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    title: "", description: "", employer: "",
    role_category: "Certified Nursing Assistant (CNA)",
    employment_type: "Full-time", shift: "Any",
    pay_range: "", location: "", address: "",
    start_asap: true, apply_url: "", contact: "", requirements: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (!authLoading && !user) nav("/login"); }, [authLoading, user, nav]);
  useEffect(() => {
    if (!editing) return;
    api.get(`/jobs/${id}`).then(({ data }) => {
      setForm({
        title: data.title || "",
        description: data.description || "",
        employer: data.employer || "",
        role_category: data.role_category || "Certified Nursing Assistant (CNA)",
        employment_type: data.employment_type || "Full-time",
        shift: data.shift || "Any",
        pay_range: data.pay_range || "",
        location: data.location || "",
        address: data.address || "",
        start_asap: Boolean(data.start_asap),
        apply_url: data.apply_url || "",
        contact: data.contact || "",
        requirements: data.requirements || "",
      });
    });
  }, [id, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.employer.trim() || !form.location.trim()) {
      return toast.error("Title, employer, location and description are required");
    }
    setSaving(true);
    try {
      const { data } = editing
        ? await api.patch(`/jobs/${id}`, { ...form, images: [] })
        : await api.post("/jobs", { ...form, images: [] });
      toast.success(editing ? "Job updated" : "Job posted");
      nav(`/jobs/${data.id}`);
    } catch {
      toast.error("Failed to save job");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">{editing ? "Edit" : "New job posting"}</div>
      <h1 className="font-display text-4xl text-[#1E3A2F] mt-1">{editing ? "Edit job" : "Post a healthcare job"}</h1>
      <p className="text-sm text-[#5C5C5C] mt-2">Reach caregivers, nurses and healthcare staff in the African community across Washington.</p>

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-[#E2DED0] rounded-2xl p-6 sm:p-8">
        <div>
          <Label>Job title</Label>
          <Input data-testid="job-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Overnight caregiver — Kent adult family home" />
        </div>
        <div>
          <Label>Employer / Facility</Label>
          <Input data-testid="job-employer" value={form.employer} onChange={(e) => set("employer", e.target.value)} placeholder="Baraka Care AFH" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Role</Label>
            <Select value={form.role_category} onValueChange={(v) => set("role_category", v)}>
              <SelectTrigger data-testid="job-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Employment type</Label>
            <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
              <SelectTrigger data-testid="job-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Shift</Label>
            <Select value={form.shift} onValueChange={(v) => set("shift", v)}>
              <SelectTrigger data-testid="job-shift"><SelectValue /></SelectTrigger>
              <SelectContent>{SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pay range (optional)</Label>
            <Input data-testid="job-pay" value={form.pay_range} onChange={(e) => set("pay_range", e.target.value)} placeholder="$22–$28 / hr" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>City / Location</Label>
            <Input data-testid="job-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Kent, WA" />
          </div>
          <div>
            <Label>Address (optional)</Label>
            <Input data-testid="job-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Care Way, Kent, WA" />
          </div>
        </div>

        <label className="inline-flex items-center gap-2">
          <Checkbox data-testid="job-asap" checked={form.start_asap} onCheckedChange={(v) => set("start_asap", Boolean(v))} />
          <span>This role starts immediately</span>
        </label>

        <div>
          <Label>Description</Label>
          <Textarea data-testid="job-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={6} placeholder="What does a typical shift look like? Team, patients, culture." />
        </div>
        <div>
          <Label>Requirements (optional)</Label>
          <Textarea data-testid="job-requirements" value={form.requirements} onChange={(e) => set("requirements", e.target.value)} rows={4} placeholder="Certifications, language skills, background check, etc." />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Apply link (optional)</Label>
            <Input data-testid="job-apply-url" value={form.apply_url} onChange={(e) => set("apply_url", e.target.value)} placeholder="https://…" />
          </div>
          <div>
            <Label>Contact (optional)</Label>
            <Input data-testid="job-contact" value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder="Text 206-555-0142 or email hr@baraka.care" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => nav(-1)}>Cancel</Button>
          <Button data-testid="job-submit" type="submit" disabled={saving} className="abm-btn-primary rounded-full px-6">
            {saving ? "Saving…" : editing ? "Save changes" : "Publish job"}
          </Button>
        </div>
      </form>
    </div>
  );
}
